import { useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "md_last_seen:";

/**
 * Conta gli elementi "non visti" basandosi su `updated_at`/`created_at`
 * confrontato con un timestamp salvato in localStorage per la sezione.
 *
 * Pattern: ad ogni visita di una sezione chiama `markSeen(key)` per
 * azzerare il badge. Il countdown si basa sul valore corrente di
 * `last_seen` (default: epoch 0 → tutto risulta non visto al primo login).
 */
export function useUnreadCount<T extends { updated_at?: string | null; created_at?: string | null }>(
  key: string,
  items: T[] | undefined,
): number {
  const [lastSeen, setLastSeen] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key) ?? "1970-01-01T00:00:00Z";
    } catch {
      return "1970-01-01T00:00:00Z";
    }
  });

  // Risposta al markSeen di altre tab (storage event)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_PREFIX + key && e.newValue) setLastSeen(e.newValue);
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key]);

  return useMemo(() => {
    const ts = lastSeen;
    return (items ?? []).filter((it) => {
      const t = it.updated_at ?? it.created_at;
      return t && t > ts;
    }).length;
  }, [items, lastSeen]);
}

export function markSeen(key: string) {
  const ts = new Date().toISOString();
  try {
    localStorage.setItem(STORAGE_PREFIX + key, ts);
    // Notifica altre tab via custom storage event
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_PREFIX + key, newValue: ts }));
  } catch {
    // localStorage non disponibile: amen
  }
}
